import { BottomSheet, Text, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { isPast } from "date-fns";
import { useMemo } from "react";

import { LoadingButton } from "../../components/LoadingButton";
import { formatDayOfMonthWithDayName } from "../../utils/formatDayOfMonthWithDayName";
import { formatTimeRange } from "../../utils/formatTimeRange";
import { BidAmountDisplay } from "./BidAmountDisplay";
import { AuctionBottomSheetFooter } from "./Bids/AuctionBidBottomSheetFooter";
import { AuctionBottomSheetHeader } from "./Bids/AuctionBidBottomSheetHeader";
import { AuctionCountdownTimer, useAuctionTimer } from "./Bids/AuctionBidCountdownTimer";
import { AuctionShiftInfo } from "./Bids/AuctionShiftInfo";

interface ReviewBidBottomSheetProps {
  modalState: UseModalState;
  workplaceName: string;
  shiftStart: Date;
  shiftEnd: Date;
  timezone: string;
  auctionEndTime: Date;
  inputBidPay: number;
  auctionBidAmount?: number | undefined;
  isLoading?: boolean;
  isInstantPay: boolean;
  scheduledShiftDurationInHours: number;
  breakDurationInMinutes: number;
  requiresBreak: boolean;
  remainingWorkDurationInHours: number;
  minutesElapsed: number;
  onConfirmBid: () => void;
  onBack: () => void;
}

export function ReviewBidBottomSheet(props: ReviewBidBottomSheetProps) {
  const {
    modalState,
    workplaceName,
    shiftStart,
    shiftEnd,
    timezone,
    auctionEndTime,
    inputBidPay,
    auctionBidAmount,
    isLoading = false,
    isInstantPay,
    scheduledShiftDurationInHours,
    breakDurationInMinutes,
    requiresBreak,
    remainingWorkDurationInHours,
    minutesElapsed,
    onConfirmBid,
    onBack,
  } = props;

  const { isExpired, secondsLeft } = useAuctionTimer(auctionEndTime);

  const dateDisplay = useMemo(
    () => formatDayOfMonthWithDayName(shiftStart, { timezone }),
    [shiftStart, timezone]
  );

  const timeDisplay = useMemo(
    () =>
      formatTimeRange(
        { startDate: shiftStart, endDate: shiftEnd },
        { timezone, useNowForStartDate: isPast(shiftStart) }
      ),
    [shiftStart, shiftEnd, timezone]
  );

  return (
    <BottomSheet modalState={modalState}>
      <DialogContent sx={{ backgroundColor: "unset", py: 12, mt: 8 }}>
        <AuctionBottomSheetHeader title="Review your bid" onBack={onBack} />

        <Stack spacing={6} sx={{ width: "100%" }}>
          <AuctionShiftInfo
            workplaceName={workplaceName}
            dateDisplay={dateDisplay}
            timeDisplay={timeDisplay}
          />

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <AuctionCountdownTimer secondsLeft={secondsLeft} />
          </Box>

          <Box
            sx={{
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: 4,
              py: 8,
              width: "100%",
            }}
          >
            <Stack alignItems="center">
              <BidAmountDisplay bidAmount={inputBidPay} isExpired={isExpired} />
            </Stack>
          </Box>

          <Text semibold variant="body1" sx={{ textAlign: "center" }}>
            Bids are final, so confirm this is the pay rate you want before submitting.
          </Text>
        </Stack>
      </DialogContent>

      <AuctionBottomSheetFooter
        isInstantPay={isInstantPay}
        scheduledShiftDurationInHours={scheduledShiftDurationInHours}
        breakDurationInMinutes={breakDurationInMinutes}
        requiresBreak={requiresBreak}
        remainingWorkDurationInHours={remainingWorkDurationInHours}
        minutesElapsed={minutesElapsed}
        bidAmount={inputBidPay}
      >
        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          disabled={isExpired || isDefined(auctionBidAmount)}
          isLoading={isLoading}
          onClick={onConfirmBid}
        >
          {auctionBidAmount
            ? `Bid placed at ${formatDollarsAsUsd(auctionBidAmount)}/hr`
            : "Confirm bid"}
        </LoadingButton>
      </AuctionBottomSheetFooter>
    </BottomSheet>
  );
}
